import { useState } from 'react';
import { Link } from 'react-router-dom';

import Button from 'components/ui/button';
import AvisoDeFoto from 'pages/Selos/components/AvisoDeFoto';
import CartaoDeSelo from 'pages/Selos/components/CartaoDeSelo';
import ModoDaMoldura from 'pages/Selos/components/ModoDaMoldura';
import { ModoDaImagem, pedeFoto, SeloDaColecao, ultimoDeCadaTipo } from 'pages/Selos/selos-modelo';
import { useFocoNaTrocaDeFoto } from 'pages/Selos/useFocoNaTrocaDeFoto';
import { TITULO_DE_SECAO } from 'styles';

interface SecaoSelosProps {
  /** A coleção de todos os ciclos, a mesma da galeria (AJH-10); ausente quando a carga falhou. */
  selos?: SeloDaColecao[];
  /** Funci com foto enviada (`avatar_type === 1`): só assim há moldura com a foto para mostrar e baixar. */
  temFoto: boolean;
  onTentarDeNovo: () => void;
  /** Abre o editor de foto na página (AJH-48, AJ2-24). */
  onAtualizarFoto: () => void;
}

/**
 * Selos no Meu Perfil, no layout do v2: título, alternância e "Ver todos os selos" numa linha; o último selo de cada
 * tipo (até 3), cada um só com a moldura e o "Baixar" (AJ2-19 a AJ2-21). A coleção inteira fica em "Ver todos os selos".
 */
export default function SecaoSelos({ selos, temFoto, onTentarDeNovo, onAtualizarFoto }: SecaoSelosProps) {
  const [modo, setModo] = useState<ModoDaImagem>('com-foto');
  const refDoTitulo = useFocoNaTrocaDeFoto<HTMLHeadingElement>(temFoto, selos);

  if (!selos) {
    return (
      <section className="mt-2.5 flex flex-col gap-3">
        <h2 className={TITULO_DE_SECAO}>Selos</h2>
        <div className="flex flex-col items-center gap-3 rounded-2xl bg-surface-0 px-5 py-5 shadow-jj-01">
          <p role="alert" className="m-0 text-sm text-content">
            Não foi possível carregar os selos.
          </p>
          <Button variant="secundario" size="sm" onClick={onTentarDeNovo}>
            Tentar de novo
          </Button>
        </div>
      </section>
    );
  }

  const visiveis = ultimoDeCadaTipo(selos);

  return (
    <section className="mt-2.5 flex flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h2 ref={refDoTitulo} tabIndex={-1} className={`${TITULO_DE_SECAO} focus:outline-none`}>
            Selos <span className="text-brand-blue">({selos.length})</span>
          </h2>
          <p className="m-0 text-[13px] text-content">Os últimos que você recebeu, já na moldura para compartilhar</p>
        </div>
        {selos.length > 0 && (
          <div className="ml-auto">
            <ModoDaMoldura modo={modo} onModo={setModo} />
          </div>
        )}
        <Link
          to="/selos"
          className="flex items-center gap-2 whitespace-nowrap font-display text-xs font-bold uppercase tracking-[0.05em] text-brand-blue-dark"
        >
          Ver todos os selos
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M5 12h14" />
            <path d="m13 6 6 6-6 6" />
          </svg>
        </Link>
      </div>

      {selos.length === 0 ? (
        <p className="m-0 rounded-2xl bg-surface-0 py-8 text-center text-sm text-content-soft shadow-jj-01">Você ainda não possui selos</p>
      ) : (
        <>
          {pedeFoto(visiveis, temFoto, modo) && <AvisoDeFoto onAtualizarFoto={onAtualizarFoto} />}

          <ul aria-label="Selos conquistados" className="m-0 grid list-none grid-cols-1 gap-[18px] p-0 sm:grid-cols-3">
            {visiveis.map(selo => (
              <li key={selo.id}>
                <CartaoDeSelo selo={selo} temFoto={temFoto} modo={modo} onAtualizarFoto={onAtualizarFoto} />
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
